import React, { useMemo } from 'react';
import AnalysisCard from '../AnalysisCard';
import useLanguage from '@op/hooks/useLanguage';
import { useSummaryKeywordDrillStats } from '../../hook';
import { percentageFormat, reviewsFormat } from '@op/utils/value';
import { formatDateFunc } from '@op/utils/time';
import SteamRatingCard from '../SteamRatingCard';
import SvgIcon from '@home/components/SvgIcon';
import moment from 'moment';
import './index.scss';
import BubbleCommon, {
  keywordDrillUrlSteamFilteredReviewsRatingTips,
} from '@op/components/BubbleCommon';
import { DBO_TM } from '@op/utils/format';

const PCMetricsCard = (props: any) => {
  const { filter, tab, gameInfo, wholeGameInfo } = props;
  const { formatMessage } = useLanguage();

  const params = useMemo(
    () => ({
      ...filter,
      channel: [tab],
      tab_type: 'pc',
      need_link: true,
      is_today: DBO_TM.isToday(filter),
    }),
    [filter, tab],
  );

  const { data, loading, error } = useSummaryKeywordDrillStats({
    filter: params,
    gameInfo,
  });

  const steamStats = useMemo(() => {
    const steamData = data?.steam;
    const goodReviewsValue: number =
      steamData?.find((item) => item.key === 'select_recommend')?.review || 0;
    const badReviewsValue: number =
      steamData?.find((item) => item.key === 'select_non_recommend')?.review ||
      0;
    const reviewsValue =
      steamData?.find((item) => item.key === 'select_all_review')?.review || 0;
    const goodRatio = (goodReviewsValue / reviewsValue) * 100;
    const badRatio = (badReviewsValue / reviewsValue) * 100;
    return {
      goodRatio,
      goodReviewsValue,
      badRatio,
      badReviewsValue,
    };
  }, [data]);

  const ThumbsElement: React.FC<{
    steamStats: {
      goodRatio: number;
      goodReviewsValue: number;
      badRatio: number;
      badReviewsValue: number;
    };
  }> = ({ steamStats }) => {
    const { goodRatio, goodReviewsValue, badRatio, badReviewsValue } =
      steamStats;
    return (
      <div className="positive-mixed-box">
        <div className="positive-mixed-box-item">
          <SvgIcon type="good-icon" className="positive-mixed-box-icon" />
          <div className="positive-mixed-box-progress">
            <div
              className="positive-mixed-box-progress-inner"
              style={{ width: `${goodRatio}%` }}
            ></div>
          </div>
          <div className="positive-mixed-box-progress-data">{`${goodReviewsValue}(${percentageFormat(
            goodRatio,
            '-',
            0,
          )})`}</div>
        </div>
        <div className="positive-mixed-box-item positive-mixed-box-item-nogood">
          <SvgIcon type="no-good-icon" className="positive-mixed-box-icon" />
          <div className="positive-mixed-box-progress">
            <div
              className="positive-mixed-box-progress-inner"
              style={{ width: `${badRatio}%` }}
            ></div>
          </div>
          <div className="positive-mixed-box-progress-data">{`${badReviewsValue}(${percentageFormat(
            badRatio,
            '-',
            0,
          )})`}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="dbo-pc-metrics-card-wrapper">
      {/* Filtered Reviews */}
      <AnalysisCard
        className="dbo-analysis-card--filter-reviews"
        value={data?.filtered_reviews || 0}
        title={
          <BubbleCommon
            showIcon={false}
            tipsMap={keywordDrillUrlSteamFilteredReviewsRatingTips}
          >
            {formatMessage({ id: 'Filtered Reviews' })}
          </BubbleCommon>
        }
        percentValue={data?.filtered_reviews_ratio || 0}
        footer={
          <div className="dbo-pc-metrics-card filter-reviews-footer">
            <div>
              {formatMessage(
                { id: 'out of {total} Steam Reviews' },
                {
                  total: (
                    <span className="footer-value-text">
                      {reviewsFormat(data?.total_reviews || 0)}
                    </span>
                  ),
                },
              )}
            </div>
            <ThumbsElement steamStats={steamStats} />
          </div>
        }
        loading={loading}
        loadingHeight={113}
        // empty={false}
        bubbleProps={{
          showContent: !!data?.filtered_reviews,
          oldTime: [data?.link_of_start_time, data?.link_of_end_time],
          newTime: [data?.start_time, data?.end_time],
          oldValue: data?.filtered_reviews_old,
          newValue: data?.filtered_reviews,
          formateDate: formatDateFunc(filter?.date_type || 'daily'),
          formateValue: reviewsFormat,
        }}
      />

      {/* Steam Ratings */}
      <SteamRatingCard
        data={data}
        loadingHeight={167}
        loading={loading}
        error={error}
        filter={filter}
        wholeGameInfo={wholeGameInfo}
      />
    </div>
  );
};

export default PCMetricsCard;
